/**
 * Modal for creating or updating a Stripe Terminal Location on a connected
 * account. Required before merchants can connect a physical card reader.
 *
 * Used in two places:
 *   - Auto-opened on PaymentProcessorList after a brand-new processor finishes
 *     Stripe-hosted onboarding (so the merchant configures their location right
 *     after the account becomes active).
 *   - Opened from PaymentProcessorEdit's StripeConnectStatusPanel via an
 *     Add/Edit Terminal location button.
 *
 * Writes the resulting tml_... id into config.platform_account_location_id.
 */

import { useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import {
  TerminalLocationAddress,
  upsertTerminalLocation,
  whereIsTerminalLocation,
  relocateTerminalLocationToPlatform,
  adoptPlatformTerminalLocation,
  TerminalLocationWhereabouts,
} from "./api";

interface Props {
  processorId: number;
  processorName: string;
  /** Existing location id (if updating) so we can show context. */
  existingLocationId?: string | null;
  /** Optional initial values, e.g. when editing an existing location. */
  initial?: {
    displayName?: string;
    address?: Partial<TerminalLocationAddress>;
  };
  onClose: () => void;
  /** Fired after the upsert succeeds, with the new/updated location id. */
  onSaved?: (locationId: string) => void;
}

const COUNTRIES = [
  { code: "US", label: "United States" },
  { code: "CA", label: "Canada" },
  { code: "GB", label: "United Kingdom" },
  { code: "AU", label: "Australia" },
];

export const TerminalLocationModal = ({
  processorId,
  processorName,
  existingLocationId,
  initial,
  onClose,
  onSaved,
}: Props) => {
  const [displayName, setDisplayName] = useState(
    initial?.displayName || processorName || ""
  );
  // Which Stripe account the recorded location is actually on. Answered by
  // the backend probing Stripe, because the database cannot tell you.
  const [whereabouts, setWhereabouts] =
    useState<TerminalLocationWhereabouts | null>(null);
  useEffect(() => {
    if (!existingLocationId) return;
    let cancelled = false;
    whereIsTerminalLocation(processorId)
      .then((r) => {
        if (!cancelled) setWhereabouts(r);
      })
      .catch((e) => {
        if (!cancelled)
          setWhereabouts({
            account: "unknown",
            locationId: existingLocationId,
            error: e?.message,
          });
      });
    return () => {
      cancelled = true;
    };
  }, [processorId, existingLocationId]);

  const [line1, setLine1] = useState(initial?.address?.line1 || "");
  const [line2, setLine2] = useState(initial?.address?.line2 || "");
  const [city, setCity] = useState(initial?.address?.city || "");
  const [stateVal, setStateVal] = useState(initial?.address?.state || "");
  const [postalCode, setPostalCode] = useState(initial?.address?.postal_code || "");
  const [country, setCountry] = useState(initial?.address?.country || "US");
  const [busy, setBusy] = useState(false);

  // This venue's location is on the merchant's account but belongs on Ronin's.
  // In that state the primary button MOVES it rather than editing an address
  // that is in the wrong place to begin with.
  const needsMoveToPlatform =
    whereabouts?.expected === "platform" && whereabouts?.account === "connected";

  const handleMoveToPlatform = async () => {
    setBusy(true);
    try {
      // Address is optional here on purpose — the backend copies the existing
      // location's address unless every field is filled in, so an untouched
      // form keeps the address that is already on receipts.
      const filled =
        line1.trim() && city.trim() && postalCode.trim() && country.trim();
      const result = await relocateTerminalLocationToPlatform(
        processorId,
        displayName.trim() || undefined,
        filled
          ? {
              line1: line1.trim(),
              line2: line2.trim() || undefined,
              city: city.trim(),
              state: stateVal.trim() || undefined,
              postal_code: postalCode.trim(),
              country,
            }
          : undefined
      );
      toast.success(
        `Moved to Ronin's account — address ${result.addressSource}` +
          (result.oldLocationRemoved ? ", old location removed" : "")
      );
      if (!result.oldLocationRemoved && result.oldLocationRemoveError) {
        toast.error(
          `The old location could not be removed: ${result.oldLocationRemoveError}. Delete it in Stripe so nobody picks it for a deploy group.`
        );
      }
      onSaved?.(result.locationId);
      onClose();
    } catch (e: any) {
      toast.error(e?.message || "Could not move the location");
    } finally {
      setBusy(false);
    }
  };

  // Already sitting on Ronin's account but never recorded as such. Adopting it
  // is right; creating a second platform location would leave a duplicate in
  // the list ops picks from for deploy groups.
  const needsAdopt =
    whereabouts?.account === "platform" &&
    whereabouts?.recordedAsDestination === false;

  const handleAdopt = async () => {
    setBusy(true);
    try {
      const r = await adoptPlatformTerminalLocation(processorId);
      toast.success(`Recorded as running through Ronin — ${r.reason}`);
      onSaved?.(r.locationId);
      onClose();
    } catch (e: any) {
      toast.error(e?.message || "Could not record this venue");
    } finally {
      setBusy(false);
    }
  };

  const handleSave = async () => {
    if (!displayName.trim()) return toast.error("Display name is required");
    if (!line1.trim()) return toast.error("Address line 1 is required");
    if (!city.trim()) return toast.error("City is required");
    if (!postalCode.trim()) return toast.error("Postal code is required");
    if (country === "US" && !stateVal.trim())
      return toast.error("State is required for US locations");

    setBusy(true);
    try {
      const result = await upsertTerminalLocation(processorId, displayName.trim(), {
        line1: line1.trim(),
        line2: line2.trim() || undefined,
        city: city.trim(),
        state: stateVal.trim() || undefined,
        postal_code: postalCode.trim(),
        country: country.trim().toUpperCase(),
      });
      toast.success(existingLocationId ? "Location updated" : "Location created");
      onSaved?.(result.id);
      onClose();
    } catch (err: any) {
      toast.error(err.message || "Failed to save location");
    } finally {
      setBusy(false);
    }
  };

  const inputCls =
    "w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500";

  return (
    <>
      <div
        className="fixed inset-0 bg-black/30 z-40"
        onClick={!busy ? onClose : undefined}
      />
      <div className="fixed inset-0 z-50 flex items-start justify-center pt-16 pointer-events-none overflow-y-auto">
        <div
          className="pointer-events-auto w-full max-w-2xl bg-white border border-gray-200 rounded-lg shadow-lg p-5 m-4"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="text-base font-semibold mb-1">
            {existingLocationId ? "Edit Terminal Location" : "Add Terminal Location"}
          </div>
          <div className="text-xs text-gray-500 mb-4">
            Stripe Terminal requires a physical location for each card reader.
            This address shows up on receipts and on Stripe's compliance records
            for the connected account.
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Display name *
              </label>
              <input
                className={inputCls}
                placeholder="e.g. Main truck, Booth 12, Downtown shop"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                disabled={busy}
                autoFocus
              />
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Address line 1 *
              </label>
              <input
                className={inputCls}
                placeholder="123 Main St"
                value={line1}
                onChange={(e) => setLine1(e.target.value)}
                disabled={busy}
              />
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Address line 2
              </label>
              <input
                className={inputCls}
                placeholder="Suite, unit, etc."
                value={line2}
                onChange={(e) => setLine2(e.target.value)}
                disabled={busy}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                City *
              </label>
              <input
                className={inputCls}
                value={city}
                onChange={(e) => setCity(e.target.value)}
                disabled={busy}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                State / Province {country === "US" ? "*" : ""}
              </label>
              <input
                className={inputCls}
                placeholder={country === "US" ? "CA, NY, etc." : "Optional"}
                value={stateVal}
                onChange={(e) => setStateVal(e.target.value)}
                disabled={busy}
                maxLength={country === "US" ? 2 : undefined}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Postal code *
              </label>
              <input
                className={inputCls}
                value={postalCode}
                onChange={(e) => setPostalCode(e.target.value)}
                disabled={busy}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Country *
              </label>
              <select
                className={inputCls}
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                disabled={busy}
              >
                {COUNTRIES.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {existingLocationId && (
            <div className="mt-3 text-xs">
              <div className="text-gray-400 font-mono">
                Existing location id: {existingLocationId}
              </div>
              {/* WHICH ACCOUNT this location is on. Ops could not tell before,
                  and the answer changes what the address edit does and where
                  the deploy group has to look for it. */}
              {whereabouts === null ? (
                <div className="mt-1 text-gray-400">Checking which account…</div>
              ) : whereabouts.account === "platform" ? (
                <div className="mt-1 rounded border border-indigo-200 bg-indigo-50 px-2 py-1 text-indigo-800">
                  On <strong>Ronin's platform account</strong>. This venue's card
                  taps run through Ronin. Its deploy group entry is picked from
                  Ronin's own location list — there is no account to search.
                </div>
              ) : whereabouts.account === "connected" ? (
                <div className="mt-1 rounded border border-gray-200 bg-gray-50 px-2 py-1 text-gray-700">
                  On <strong>the merchant's own Stripe account</strong>
                  {whereabouts.connectedAccountId ? (
                    <span className="font-mono"> ({whereabouts.connectedAccountId})</span>
                  ) : null}
                  . Money goes straight to them. For the deploy group, search
                  that account id and pick this location.
                </div>
              ) : whereabouts.account === "missing" ? (
                <div className="mt-1 rounded border border-red-200 bg-red-50 px-2 py-1 text-red-700">
                  <strong>Not found on either account.</strong> This row names a
                  location that no longer exists in Stripe, so no reader can be
                  registered against it. Create a new one.
                </div>
              ) : whereabouts.account === "unknown" ? (
                <div className="mt-1 rounded border border-amber-200 bg-amber-50 px-2 py-1 text-amber-800">
                  Could not determine which account holds this location
                  {whereabouts.error ? ` (${whereabouts.error})` : ""}.
                </div>
              ) : null}
              {/* WHERE IT SHOULD BE. Knowing where a location is only helps
                  if you also know where it belongs — and for the legacy
                  fee-locked accounts those differ, which is the entire reason
                  this project exists. */}
              {whereabouts &&
                whereabouts.expected === "platform" &&
                whereabouts.account === "connected" && (
                  <div className="mt-1 rounded border border-red-300 bg-red-50 px-2 py-1 text-red-800">
                    <strong>This should be on Ronin's platform account.</strong>{" "}
                    {whereabouts.expectedReason ||
                      "This account cannot bill Stripe's fee to Ronin as-is."}{" "}
                    The button below moves it: an identical location is created
                    on Ronin's account with <strong>this same address</strong>,
                    and the old one is removed. Leave the address fields blank
                    to keep the address exactly as it is today.
                  </div>
                )}
              {whereabouts &&
                whereabouts.expected === "connected" &&
                whereabouts.account === "platform" && (
                  <div className="mt-1 rounded border border-amber-300 bg-amber-50 px-2 py-1 text-amber-800">
                    <strong>
                      This account does not need to run through Ronin.
                    </strong>{" "}
                    {whereabouts.expectedReason ||
                      "The platform is already billed for this account's Stripe fees."}{" "}
                    Its location could live on the merchant's own account, which
                    keeps them as the merchant of record.
                  </div>
                )}
              {whereabouts &&
                whereabouts.expected === "unknown" &&
                whereabouts.account !== "none" && (
                  <div className="mt-1 rounded border border-gray-200 bg-gray-50 px-2 py-1 text-gray-600">
                    Could not work out where this location should be
                    {whereabouts.expectedReason
                      ? `: ${whereabouts.expectedReason}`
                      : "."}
                  </div>
                )}
              {whereabouts?.account === "platform" &&
                whereabouts.recordedAsDestination === false && (
                  <div className="mt-1 rounded border border-amber-200 bg-amber-50 px-2 py-1 text-amber-800">
                    <strong>
                      This location is already on Ronin's account, but the venue
                      is not recorded as running through Ronin.
                    </strong>{" "}
                    The button below records it — nothing is created or deleted,
                    so there will be no duplicate in Ronin's location list.
                  </div>
                )}
            </div>
          )}

          <div className="flex items-center justify-end gap-2 mt-5">
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              className="whitespace-nowrap rounded border border-gray-300 bg-white text-gray-700 text-sm font-medium px-4 py-2 hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={
                busy
                  ? undefined
                  : needsMoveToPlatform
                  ? handleMoveToPlatform
                  : needsAdopt
                  ? handleAdopt
                  : handleSave
              }
              disabled={busy}
              className="whitespace-nowrap rounded bg-primary-600 text-white text-sm font-medium px-4 py-2 hover:bg-primary-700 disabled:opacity-50"
            >
              {busy
                ? needsMoveToPlatform
                  ? "Moving..."
                  : needsAdopt
                  ? "Recording..."
                  : "Saving..."
                : needsMoveToPlatform
                ? "Move location to Ronin's account"
                : needsAdopt
                ? "Record this venue as running through Ronin"
                : existingLocationId
                ? "Update location"
                : "Save location"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};
